import React, { useMemo } from 'react';
import { View } from 'react-native';
import { useNavigation } from '@react-navigation/native';

import { GET_PURCHASE_ORDER_LIST } from '../../../common/graphql/purchaseOrder';
import { ROLE_PERMISSIONS } from '../../../common/constants';
import { useUserContext } from '../../../common/utils/context';

import { useThemedStyles } from '../../hooks/useThemedStyles';
import Text from '../../components/text/Text';
import { EmptyState } from '../../design/components';

import PurchaseOrderList from './PurchaseOrderList';

const PurchaseOrderListView = ({ hideHeader = false }) => {
	const navigation = useNavigation();
	const styles = useThemedStyles(createStyles);
	const { hasPermission } = useUserContext();

	const canAccessPurchaseOrderManagement = useMemo(() => {
		return Boolean(hasPermission(ROLE_PERMISSIONS.VIEW_PURCHASE_ORDER_LIST_PAGE));
	}, [hasPermission]);

	const canViewPurchaseOrderDetail = useMemo(() => {
		return Boolean(hasPermission(ROLE_PERMISSIONS.VIEW_PURCHASE_ORDER_DETAIL_PAGE));
	}, [hasPermission]);

	const handleOpenDetail = useMemo(() => {
		return (purchaseOrder) => {
			if (purchaseOrder?.id && canViewPurchaseOrderDetail) {
				navigation.navigate('PurchaseOrderDetailView', { id: purchaseOrder.id });
			}
		};
	}, [canViewPurchaseOrderDetail, navigation]);

	if (!canAccessPurchaseOrderManagement) {
		return (
			<EmptyState icon="lock" title="You do not have access to purchase orders." />
		);
	}

	return (
		<View style={styles.container}>
			{
				!hideHeader && (
					<View style={styles.header}>
						<Text style={styles.heading} accessibilityRole="header">Purchase orders</Text>
						<Text style={styles.subheading}>Mark delivered quantities, then capture batches when stocking</Text>
					</View>
				)
			}
			<PurchaseOrderList
				query={GET_PURCHASE_ORDER_LIST}
				initialSortBy="created"
				initialSortDirection={-1}
				initialPerPage={20}
				initialFilter={{}}
				listId="mobile_purchase_order_list"
				customProps={{
					onPressItem: handleOpenDetail,
					canViewDetail: canViewPurchaseOrderDetail,
				}}
			/>
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	container: {
		flex: 1,
		backgroundColor: colors.canvas,
	},
	header: {
		paddingHorizontal: space.gutter,
		paddingTop: 20,
		paddingBottom: space.md,
		gap: 6,
	},
	heading: {
		...type.title,
		color: colors.text,
	},
	subheading: {
		...type.bodySmall,
		color: colors.textSecondary,
	},
});

export default PurchaseOrderListView;
